import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import withTranslator from './HOCComponents/withTranslator'
import CropMasterAcademyIcon from '../assets/img/cropMaster/academy.svg'
import CropMasterInfoIcon from '../assets/img/cropMaster/info.svg'
import AcademyBg from '../assets/img/cropMaster/academy_bg.jpg'
import AboutBg from '../assets/img/cropMaster/about_bg.jpg'
import CmPageBg from '../assets/img/cropMaster/cm_page_bg.jpg'
import themeColors from '@layers-frontend/commons/styles/themeColors'
import BlogCarrousel from './CropMasterPage/BlogCarrousel'
import CMHLogo from '../assets/img/cropMaster/crop_master_awards/CMH.svg'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import { BlogCacheProvider } from './CropMasterPage/BlogCacheContext'
import { getLocale } from '@layers-frontend/commons/store/selectors/locale'
import { hasUserRole } from '@layers-frontend/commons/store/selectors/user'
import { CROPMASTER_USER_ROLES } from '@layers-frontend/commons/constants'
import { useNavigate } from 'react-router-dom'

import papa from 'papaparse'

import {
  GlobalContainer,
  ScrollContainer,
  CropMasterPageWrapper,
  WelcomeTextWrapper,
  WelcomeText,
  ButtonsWrapper,
  RowWithVideo,
  TopButton,
  TopButtonOverlay,
  TopButtonIcon,
  VideoWrapper,
  PlayOverlay,
  PlayButton,
  IconCircle,
  ButtonLabel,
  TopButtonLink,
  ContentCenterWrapper,
  CropMasterLogo,
  VideoCloseOverlay,
  VideoModalOverlay,
  VideoModalWrapper,
  CarouselWrapper
} from './CropMasterPage/styles'

import { getYoutubeThumbnail, getBackgroundStyles, getCropMasterLinks, DEFAULT_LINKS } from './CropMasterPage/utils'

const BLOG_CROP_MASTER_CSV_CONTENT_URL = '/blog/wp-content/uploads/crop-master-onboarding.csv'

function CropMasterPage({ t }) {
  const locale = useSelector(getLocale)
  const hasCropMasterRole = useSelector(state => hasUserRole(state, CROPMASTER_USER_ROLES.ROLE_CROP_MASTER))
  const navigate = useNavigate()
  const [links, setLinks] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [hasScroll, setHasScroll] = useState(false)
  const thumbnail = getYoutubeThumbnail(links?.video && (links?.video?.[locale] || links?.video?.en))
  const { bgStyle, overlayStyle } = getBackgroundStyles(CmPageBg, themeColors)
  const [cropMasterContent, setCropMasterContent] = useState({ loading: true, data: {} })

  useEffect(() => {
    if (!hasCropMasterRole) {
      navigate('/')
    }
  }, [hasCropMasterRole, navigate])

  useEffect(() => {
    const fetchCropMasterCsv = async () => {
      try {
        const cropMasterCsvResponse = await fetch(`${BLOG_CROP_MASTER_CSV_CONTENT_URL}?nocache=${Date.now()}`)
        if (!cropMasterCsvResponse.ok) throw new Error('error-fetching-crop-master-csv')
        const cropMasterCsvData = await cropMasterCsvResponse.text()
        const { data } = papa.parse(cropMasterCsvData, { header: true })
        setCropMasterContent({ loading: false, data })
      } catch (error) {
        console.error(error)
        setCropMasterContent({ loading: false, error: error.message })
      }
    }

    const fetchCropMasterLinks = async () => {
      try {
        const _links = await getCropMasterLinks()
        setLinks(_links)
      } catch (error) {
        console.error(error)
        setLinks(DEFAULT_LINKS)
      }
    }

    fetchCropMasterCsv()
    fetchCropMasterLinks()
  }, [])

  useEffect(() => {
    const checkScroll = () => {
      const container = document.querySelector('[data-cropmaster-container]')
      if (container) {
        setHasScroll(container.scrollHeight > container.clientHeight)
      }
    }

    checkScroll()
    window.addEventListener('resize', checkScroll)
    return () => window.removeEventListener('resize', checkScroll)
  }, [])

  const content = (cropMasterContent?.data?.[0] && cropMasterContent?.data) || [{ [locale]: t('Welcome to CM') }]

  const renderButton = (href, bgimg, icon, label, gridArea) =>
    !links ? null : (
      <TopButtonLink href={href} target="_blank" rel="noopener noreferrer" area={gridArea}>
        <TopButton bgimg={bgimg}>
          <TopButtonOverlay />
          <IconCircle>
            <TopButtonIcon src={icon} alt={label} />
          </IconCircle>
          <ButtonLabel style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 3 }}>{label}</ButtonLabel>
        </TopButton>
      </TopButtonLink>
    )

  const renderVideoButton = () =>
    !links || !links?.video?.en ? null : (
      <VideoWrapper isPlaying={isPlaying} style={{ gridArea: 'video' }}>
        <img
          src={thumbnail}
          alt="Carátula vídeo"
          style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', left: 0, top: 0, zIndex: 1 }}
        />
        <PlayOverlay onClick={() => setIsPlaying(true)}>
          <PlayButton>
            <i className="fa-solid fa-play" style={{ marginLeft: 6 }}></i>
          </PlayButton>
        </PlayOverlay>
      </VideoWrapper>
    )

  const renderVideoModal = () =>
    !links || !(links?.video?.[locale] || links?.video?.en) ? null : (
      <VideoModalOverlay onClick={() => setIsPlaying(false)}>
        <VideoModalWrapper onClick={e => e.stopPropagation()}>
          <div
            style={{
              position: 'relative',
              width: '90vw',
              maxWidth: 900,
              aspectRatio: '16/9',
              background: '#000',
              borderRadius: 12,
              overflow: 'hidden',
              boxShadow: '0 4px 32px #0005'
            }}
          >
            <iframe
              width="100%"
              height="100%"
              src={(() => {
                const url = links?.video?.[locale] || links?.video?.en
                if (!url) return ''
                return url.includes('?') ? url + '&autoplay=1' : url + '?autoplay=1'
              })()}
              title="Crop Master Introduction Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none'
              }}
            />
          </div>
        </VideoModalWrapper>
      </VideoModalOverlay>
    )

  return (
    <BlogCacheProvider>
      <GlobalContainer>
        <div style={bgStyle} />
        <div style={overlayStyle} />
        <ScrollContainer>
          <CropMasterPageWrapper>
            <CropMasterLogo src={CMHLogo} alt="Crop Master" />
            {isPlaying && <VideoCloseOverlay onClick={() => setIsPlaying(false)} />}
            <ContentCenterWrapper>
              <RowWithVideo isPlaying={isPlaying}>{!isPlaying && <>{renderVideoButton()}</>}</RowWithVideo>
              <WelcomeTextWrapper isVisible={!isPlaying}>
                {cropMasterContent.loading ? t('loading') : content.map(item => <WelcomeText hasScroll={hasScroll}>{item[locale] || item.en}</WelcomeText>)}
              </WelcomeTextWrapper>
              {isPlaying && renderVideoModal()}
            </ContentCenterWrapper>
            <ButtonsWrapper isVisible={!isPlaying}>
              {renderButton(links?.academy, AcademyBg, CropMasterAcademyIcon, t('Academy'), 'academy')}
              {renderButton(links?.about, AboutBg, CropMasterInfoIcon, t('About'), 'about')}
            </ButtonsWrapper>
            {!isPlaying && (
              <CarouselWrapper>
                <BlogCarrousel />
              </CarouselWrapper>
            )}
          </CropMasterPageWrapper>
        </ScrollContainer>
      </GlobalContainer>
    </BlogCacheProvider>
  )
}

export default withTranslator(CropMasterPage)
